import React from "react";
import { addMonths, format } from "date-fns";
import {
    flatRatePlanNames,
    METERED_PLANS,
    PLAN_CATALOG_VERSION,
} from "@common/flatRateBilling";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { currencyFormatterNoFractions } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { Card } from "@design-system/Card/Card";
import { useConstant } from "@hooks/useConstant";
import styles from "./UpgradeSubscriptionModal.module.scss";

interface Props {
    billingDate: Date;
    plan: FlatRatePlan;
    planCatalogVersion: PLAN_CATALOG_VERSION;
    isTrial: boolean;
}

export const CurrentPlanSummary: React.FC<Props> = ({
    billingDate,
    plan,
    planCatalogVersion,
    isTrial,
}) => {
    const { billingInterval, intervalAmountCents } = plan;
    const today = useConstant(() => new Date());

    const realSubscriptionEnd = addMonths(billingDate, billingInterval);

    let trialSection: React.ReactNode;

    if (isTrial) {
        trialSection = (
            <section className={styles.section}>
                <main>
                    <header className={styles.header}>
                        {flatRatePlanNames[plan.plan]} Trial
                    </header>
                    <small className={styles.info}>
                        {format(today, fullDateFullYearFormat)} -{" "}
                        {format(billingDate, fullDateFullYearFormat)}
                    </small>
                </main>

                <aside className={styles.pricing}>
                    <span className={styles.value}>
                        {currencyFormatterNoFractions.format(0)}
                    </span>
                </aside>
            </section>
        );
    }

    return (
        <Card className="p-0">
            {trialSection}

            <section className={styles.section}>
                <main>
                    <header className={styles.header}>Subscription</header>
                    <div className={styles.info}>
                        {format(billingDate, fullDateFullYearFormat)} -{" "}
                        {format(realSubscriptionEnd, fullDateFullYearFormat)}
                    </div>
                    {METERED_PLANS.includes(plan.plan) &&
                    (planCatalogVersion === PLAN_CATALOG_VERSION.V2 ||
                        planCatalogVersion === PLAN_CATALOG_VERSION.V3) ? (
                        <div className={styles.info}>
                            $50/m additional entity (Free for first 250
                            transactions per year)
                        </div>
                    ) : null}
                </main>

                <aside className={styles.pricing}>
                    <span className={styles.value}>
                        {currencyFormatterNoFractions.format(
                            fromIntToMonetary(intervalAmountCents),
                        )}
                    </span>
                    <small className={styles.info}>
                        per {billingInterval === 3 ? "quarter" : "year"}
                    </small>
                </aside>
            </section>
        </Card>
    );
};
